import { useState, useEffect, useCallback } from "react";
import { fetchJson, extractErrorMessage, extractReturnUrl } from "../utils/api";
import { navigateToPaymentUrl, shouldRedirect } from "../utils/navigation";
import {
  TransactionStatus,
  TERMINAL_STATUSES,
  ERROR_MESSAGES,
  PaymentMethod,
} from "../constants/payment";

/**
 * Custom hook for fetching and managing payment data
 */
export function usePaymentData(orderId, baseUrl, navigate) {
  const [loading, setLoading] = useState(true);
  const [extraLoading, setExtraLoading] = useState(true);
  const [loadingDuration, setLoadingDuration] = useState(2500);
  const [err, setErr] = useState(null);
  const [paymentData, setPaymentData] = useState({
    method: null,
    qrPayload: null,
    operatorName: "",
    redirectUrl: null,
    amount: null,
    merchantOrderId: null,
    returnUrl: null,
    timeLeft: 0,
    status: null,
  });

  const fetchPaymentData = useCallback(
    async (controller, mounted) => {
      setErr(null);
      setLoading(true);
      const startTime = performance.now();

      try {
        const { ok, status, statusText, body } = await fetchJson(
          `${baseUrl}/${encodeURIComponent(orderId)}`,
          {
            method: "GET",
            headers: { Accept: "application/json" },
            signal: controller.signal,
          }
        );

        const endTime = performance.now();
        const duration = Math.max(0, endTime - startTime);
        if (mounted.current) setLoadingDuration(duration);

        // Handle error response
        if (!ok) {
          const apiMessage = extractErrorMessage(body, status, statusText);
          if (mounted.current) {
            setLoading(false);
            setErr(apiMessage);
            setPaymentData((prev) => ({
              ...prev,
              returnUrl: extractReturnUrl(body),
            }));
          }
          return;
        }

        // Validate response structure
        const payload = body?.body;
        if (!payload || !payload.data) {
          const apiErr = payload?.message || ERROR_MESSAGES.INVALID_RESPONSE;
          if (mounted.current) {
            setLoading(false);
            setErr(apiErr);
          }
          return;
        }

        const data = payload.data;

        // Handle terminal states (failed/expired)
        if (TERMINAL_STATUSES.includes(data.status)) {
          if (mounted.current) {
            setLoading(false);
            setErr(ERROR_MESSAGES.PAYMENT_FAILED);
            setPaymentData((prev) => ({
              ...prev,
              qrPayload: null,
              returnUrl: data?.returnUrl || null,
            }));
          }
          return;
        }

        // Handle initiated state - retry
        if (data.status === TransactionStatus.INITIATED) {
          if (!controller.signal.aborted) {
            setTimeout(() => fetchPaymentData(controller, mounted), 3000);
          }
          return;
        }

        // Show spinner for perceived network time
        await new Promise((r) => setTimeout(r, duration));
        if (!mounted.current) return;

        setLoading(false);
        setTimeout(() => {
          if (mounted.current) setExtraLoading(false);
        }, 3000);

        // Set payment data
        const newPaymentData = {
          method: data.method,
          amount: data.amount,
          merchantOrderId: data.orderId,
          returnUrl: data.returnUrl || null,
          operatorName: data.operatorName || "",
          qrPayload: data.paymentLink || null,
          status: data.status,
          redirectUrl: null,
          timeLeft: 0,
        };

        // Calculate QR expiry time
        if (
          (data.method === PaymentMethod.QR_CODE ||
            data.method === PaymentMethod.MMQR) &&
          data.operatorName === "KBZPay"
        ) {
          const diffSecond = Math.max(
            0,
            data.expiredAt - Math.floor(Date.now() / 1000),
          );
          newPaymentData.timeLeft = diffSecond;
        } else {
          newPaymentData.redirectUrl = data.paymentLink || null;

          // Navigate if not in-app operator
          if (shouldRedirect(data.operatorName, data.method)) {
            navigateToPaymentUrl(data.paymentLink, navigate);
          }
        }

        if (mounted.current) {
          setPaymentData(newPaymentData);
        }
      } catch (e) {
        if (e?.name === "AbortError") return;
        if (mounted.current) {
          setLoading(false);
          setExtraLoading(false);
          setErr(e?.message || ERROR_MESSAGES.FETCH_FAILED);
        }
      }
    },
    [orderId, baseUrl, navigate]
  );

  useEffect(() => {
    if (!orderId) {
      setLoading(false);
      setErr(ERROR_MESSAGES.NO_ORDER_ID);
      return;
    }

    const mounted = { current: true };
    const controller = new AbortController();

    fetchPaymentData(controller, mounted);

    return () => {
      mounted.current = false;
      controller.abort();
    };
  }, [orderId, fetchPaymentData]);

  return {
    loading,
    extraLoading,
    loadingDuration,
    err,
    paymentData,
  };
}